import type { IconType } from "react-icons";
import {
  FaCoins,
  FaFileSignature,
  FaFileAlt,
  FaInfoCircle,
  FaPaperPlane,
  FaTasks,
  FaUserCheck,
  FaUserTie,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

export const BID_WIZARD_STEPS = [
  { key: "intro", icon: FaInfoCircle, live: true },
  { key: "supplier", icon: FaUserTie, live: true },
  { key: "exclusion", icon: FaUserCheck, live: false },
  { key: "qualification", icon: FaTasks, live: false },
  { key: "price", icon: FaCoins, live: true },
  { key: "documents", icon: FaFileAlt, live: false },
  { key: "cover", icon: FaFileSignature, live: false },
  { key: "review", icon: FaPaperPlane, live: true },
] as const satisfies ReadonlyArray<{ key: string; icon: IconType; live: boolean }>;

export type BidStep = (typeof BID_WIZARD_STEPS)[number]["key"];

export const BID_WIZARD_STEP_KEYS = BID_WIZARD_STEPS.map((s) => s.key) as readonly BidStep[];

export const visibleBidSteps = (collectSupplierPrice: boolean): (typeof BID_WIZARD_STEPS)[number][] =>
  BID_WIZARD_STEPS.filter((s) => s.key !== "price" || collectSupplierPrice);

export const visibleBidStepKeys = (collectSupplierPrice: boolean): BidStep[] =>
  visibleBidSteps(collectSupplierPrice).map((s) => s.key);

export const isLiveStep = (key: BidStep): boolean => BID_WIZARD_STEPS.find((s) => s.key === key)?.live ?? false;

export const useBidStepLabel = (): ((key: BidStep) => string) => {
  const { t } = useTranslation();
  return (key) => {
    switch (key) {
      case "intro":
        return t("About the procurement");
      case "supplier":
        return t("About the supplier");
      case "price":
        return t("Price");
      case "exclusion":
        return t("Grounds for exclusion");
      case "qualification":
        return t("Qualification requirements");
      case "documents":
        return t("Offer documents");
      case "cover":
        return t("Cover letter");
      case "review":
        return t("Summary and submission");
    }
  };
};

export const bidStepNeighbors = (
  current: BidStep,
  collectSupplierPrice: boolean,
): { previous: BidStep | undefined; next: BidStep | undefined } => {
  const keys = visibleBidStepKeys(collectSupplierPrice);
  const idx = keys.indexOf(current);
  return {
    previous: idx > 0 ? keys[idx - 1] : undefined,
    next: idx >= 0 && idx < keys.length - 1 ? keys[idx + 1] : undefined,
  };
};
